import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { Badge, Button, Card, Spinner } from "../../shared";
import { useGetSuperAdminDashboardSummaryQuery } from "../../redux/services/admin/adminApi";

const menuItems = [
  {
    title: "Customers",
    path: "/admin/customers",
    desc: "All attune customer profiles",
  },
  {
    title: "Feature Flags",
    path: "/admin/feature-flags",
    desc: "Manage system defaults, tier packaging, and per-tenant overrides",
  },
  {
    title: "App health",
    path: "/admin/health",
    desc: "Service health, release progression, and safeguards",
  },
  {
    title: "Users",
    path: "/admin/users",
    desc: "All attune users profiles",
  },
  {
    title: "Audit logs",
    path: "/admin/audit-logs",
    desc: "Security and compliance events across tenants",
  },
  {
    title: "Support",
    path: "/admin/feedback",
    desc: "All attune customer tickets",
  },
  {
    title: "Leads",
    path: "/admin/leads",
    desc: "Marketing leads from the public sign-up form",
  },
  {
    title: "CSV Handler",
    path: "/admin/csv-handler",
    desc: "Process multiple CSV files",
  },
];

const statCards = [
  {
    id: "tenants",
    label: "Tenants",
    valueKey: "tenants",
    helper: "All customer organizations",
    icon: BuildingOffice2Icon,
    accent: "bg-primary-600",
    iconClass: "bg-primary-50 text-primary-700",
  },
  {
    id: "activeTenants",
    label: "Active",
    valueKey: "activeTenants",
    helper: "Currently activated tenants",
    icon: CheckCircleIcon,
    accent: "bg-emerald-600",
    iconClass: "bg-emerald-50 text-emerald-700",
  },
  {
    id: "billingAttentionTenants",
    label: "Billing Attention",
    valueKey: "billingAttentionTenants",
    helper: "Past due or canceled billing",
    icon: CreditCardIcon,
    accent: "bg-amber-500",
    iconClass: "bg-amber-50 text-amber-700",
  },
  {
    id: "openFeedback",
    label: "Open Support",
    valueKey: "openFeedback",
    helper: "Unresolved feedback threads",
    icon: ChatBubbleLeftRightIcon,
    accent: "bg-violet-600",
    iconClass: "bg-violet-50 text-violet-700",
  },
];

const DashboardMetricCard = ({
  label,
  value,
  helper,
  icon: Icon,
  accent,
  iconClass,
}) => (
  <Card className="relative h-full overflow-hidden border-gray-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
    <div className={`absolute inset-x-0 top-0 h-1 ${accent}`} />
    <div className="flex items-start justify-between gap-3 pt-1">
      <div className="min-w-0 space-y-1.5 sm:space-y-2">
        <p className="truncate text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="text-2xl font-semibold text-gray-950 sm:text-3xl">
          {value}
        </p>
        <p className="text-xs leading-snug text-gray-500 sm:text-sm">
          {helper}
        </p>
      </div>
      {Icon ? (
        <div className={`rounded-xl p-2 ${iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
      ) : null}
    </div>
  </Card>
);

const DashboardShortcutCard = ({ item, onNavigate }) => (
  <div
    onClick={() => onNavigate(item.path)}
    className="group block cursor-pointer rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-100 hover:shadow-md sm:p-4"
  >
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="rounded-lg bg-gradient-to-br from-primary-50 to-cyan-50 px-2 py-1 text-xs font-semibold text-primary-700">
        {item.title.slice(0, 2)}
      </div>
      <ArrowRightIcon className="h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
    </div>
    <h5 className="mb-1 text-sm font-bold tracking-tight text-gray-950 sm:text-base">
      {item.title}
    </h5>
    <p className="text-xs font-medium leading-snug text-gray-500 sm:text-sm">
      {item.desc}
    </p>
  </div>
);

const RecentCustomerCard = ({ customer, onOpenTenant, onOpenBilling }) => (
  <Card className="p-2 sm:p-3">
    <div className="flex flex-col gap-3">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="min-w-0 break-words text-sm font-medium text-gray-900 sm:text-base">
            {customer?.name}
          </h3>
          <Badge color={customer?.active ? "green" : "yellow"}>
            {customer?.active ? "Active" : "Pending"}
          </Badge>
          <Badge color={customer?.onboardingStatus?.color || "gray"}>
            {customer?.onboardingStatus?.label || "Draft"}
          </Badge>
        </div>
        <p className="break-words text-xs text-gray-500 sm:text-sm">
          {customer?.email || "No customer email"}
        </p>
        <div className="grid grid-cols-2 gap-1 text-xs text-gray-500 sm:text-sm">
          <span>Plan: {customer?.planTier || "tier1"}</span>
          <span>Release: {customer?.releaseChannel || "stable"}</span>
          <span>Users: {customer?.stats?.totalUsers || 0}</span>
          <span>Billing: {customer?.paymentStatus || "pending"}</span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 sm:flex-row">
        <Button
          type="alternative"
          size="xs"
          className="w-full justify-center sm:w-auto"
          onClick={() => onOpenBilling(customer)}
        >
          Billing
        </Button>
        <Button
          size="xs"
          className="w-full justify-center sm:w-auto"
          onClick={() => onOpenTenant(customer)}
        >
          Open tenant
        </Button>
      </div>
    </div>
  </Card>
);

const SuperAdmin = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } =
    useGetSuperAdminDashboardSummaryQuery();
  const summary = data?.summary || {};
  const totals = summary?.totals || {};
  const recentCustomers = Array.isArray(summary?.recentCustomers)
    ? summary.recentCustomers
    : [];

  function handleNavigate(path) {
    navigate(path);
  }

  function openTenant(customer) {
    navigate(`/admin/customers/${customer?._id}`);
  }

  function openBilling(customer) {
    navigate(`/admin/customers/${customer?._id}?customer-show=Billing`);
  }

  return (
    <div className="space-y-4 bg-gradient-to-b from-gray-50 to-white p-2 sm:space-y-6 sm:p-3">
      <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
        {statCards.map((item) => (
          <DashboardMetricCard
            key={item.id}
            label={item.label}
            value={isLoading ? "..." : totals?.[item.valueKey] || 0}
            helper={item.helper}
            icon={item.icon}
            accent={item.accent}
            iconClass={item.iconClass}
          />
        ))}
      </div>

      <div>
        <div className="mb-2 flex items-end justify-between gap-3 sm:mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-950">
              Admin Shortcuts
            </h2>
            <p className="text-xs text-gray-500">
              Fast paths into platform management.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-4">
          {menuItems.map((item) => (
            <DashboardShortcutCard
              key={item.path}
              item={item}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </div>

      <div className="xl:w-1/2">
        <Card className="border-gray-100 bg-white/95 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-medium text-gray-900">
                Recent Tenants
              </h2>
              <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                Latest organizations and rollout status.
              </p>
            </div>
            {isFetching && !isLoading ? (
              <div className="text-xs text-gray-500 sm:text-sm">
                Refreshing...
              </div>
            ) : null}
          </div>

          <div className="mt-3 space-y-2">
            {isLoading ? (
              <Spinner />
            ) : isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                Unable to load the super admin dashboard right now.
              </div>
            ) : recentCustomers.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                No tenants are available yet.
              </div>
            ) : (
              recentCustomers.map((customer) => (
                <RecentCustomerCard
                  key={customer._id}
                  customer={customer}
                  onOpenTenant={openTenant}
                  onOpenBilling={openBilling}
                />
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdmin;
